import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment-timezone';
import CommonHeader from '../../../util_components/common_header';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import CustomDatePicker from '../../../util_components/custom_date_picker';
import Paginator from '../../../util_components/paginator';

import { day_format_year, time_format, date_drop_for_report } from '../../../../constants';
import { get_api } from '../../../../redux/api_funcs';
import * as actions from '../../../../redux/action_creators';
import { get_api_url } from '../../../../utils/urls';
import EventsModal from './events_modal';
import {
  parse_filters,
  build_search,
  offset_for_page,
  utc_window,
  PAGE_SIZE,
  DEFAULT_DATE_FILTER
} from './filters';
import {
  step_label,
  category_label,
  origins_label,
  fmt_amount,
  fmt_datetime,
  reason_label,
  student_label,
  actionability_label
} from './helpers';

import 'react-datepicker/dist/react-datepicker.css';

class PaymentFailures extends Component {
  constructor(props) {
    super(props);
    // The URL is the source of truth on entry, so a shared link lands on the same filters.
    this.state = {
      loading: true,
      error: false,
      rows: [],
      total: 0,
      open_group: null,
      ...parse_filters(props.location.search, moment().format('YYYY-MM-DD'))
    };
  }

  componentDidMount() {
    this.load_data();
  }

  // Mirrors filters into the URL; replace, not push, so paging doesn't fill the back button.
  sync_url = () => {
    const { date_filter_type, start_date, end_date, page } = this.state;
    const search = build_search({ date_filter_type, start_date, end_date, page });
    if (search !== this.props.location.search) {
      this.props.history.replace({ pathname: this.props.location.pathname, search });
    }
  };

  load_data = (page = this.state.page) =>
    this.setState({ loading: true, error: false, page }, () => {
      this.sync_url();
      const { date_filter_type, start_date, end_date } = this.state;

      // Real UTC instants with an exclusive end — the presets disagree about whether their end is
      // inclusive, so normalising here is what keeps a range from returning an extra day.
      const window = utc_window(date_filter_type, start_date, end_date, this.props.iana_timezone);
      const qp = new URLSearchParams();
      qp.set('start_date', window.start);
      qp.set('end_date', window.end);
      qp.set('limit', String(PAGE_SIZE));
      qp.set('offset', String(offset_for_page(page)));

      const url = get_api_url(`/v2/cms_user/payment_failures?${qp.toString()}`, false, true);

      get_api(url, true)
        .then((res) => {
          const data = (res && res.data && res.data.data) || {};
          this.setState({
            loading: false,
            error: false,
            rows: data.rows || [],
            total: data.total || 0
          });
        })
        .catch((e) => {
          let message;
          try {
            message = e.response.data.message;
          } catch (err) {
            message = 'Some error occured. Please contact dev team.';
          }
          if (typeof message !== 'string') message = 'Some error occured. Please contact dev team.';
          this.props.set_notification_variable(true, 'error', message);
          // Rows and total are left alone: an empty table here would read as "no failures", which
          // is the one thing this report must never say when it does not know.
          this.setState({ loading: false, error: true });
        });
    });

  // The select's own blank option is one click away and would otherwise send an empty filter type.
  change_handler = (e) =>
    this.setState({ [e.target.name]: e.target.value || DEFAULT_DATE_FILTER, page: 1 }, () =>
      this.load_data(1)
    );

  handle_date_change = (key, value) =>
    this.setState({ [key]: moment(value).format('YYYY-MM-DD'), page: 1 }, () => this.load_data(1));

  render_filters = () => {
    const { date_filter_type, start_date, end_date } = this.state;
    return (
      <div className="flex flex-wrap items-end gap-3 justify-center mb-3">
        <CustomSingleSelect
          label="Date range"
          options={date_drop_for_report}
          name="date_filter_type"
          onChange={this.change_handler}
          value={date_filter_type}
          style={{ width: 170 }}
        />
        {date_filter_type === 'CUSTOM' ? (
          <>
            <CustomDatePicker
              label="Start Date"
              value={start_date}
              handleChange={(val) => this.handle_date_change('start_date', val)}
              maxDate={end_date}
            />
            <CustomDatePicker
              label="End Date"
              value={end_date}
              handleChange={(val) => this.handle_date_change('end_date', val)}
              minDate={start_date}
              maxDate={moment().format('YYYY-MM-DD')}
            />
          </>
        ) : null}
      </div>
    );
  };

  render_rows = () =>
    this.state.rows.map((row) => (
      <tr key={row.group_id}>
        <td>
          {fmt_datetime(row.last_attempt, this.props.iana_timezone, day_format_year)}
          <div className="text-xs">
            {fmt_datetime(row.last_attempt, this.props.iana_timezone, time_format)}
          </div>
        </td>
        <td>
          {row.student_uuid ? (
            <a
              href={`/studentprofile?id=${encodeURIComponent(row.student_uuid)}`}
              target="_blank"
              rel="noopener noreferrer"
              title="Open student profile in a new tab"
            >
              {student_label(row)}
            </a>
          ) : (
            '—'
          )}
        </td>
        <td>{row.event_count}</td>
        <td>{step_label(row.step)}</td>
        <td>{reason_label(row)}</td>
        <td>
          {category_label(row.error_category)}
          <div className="text-xs">{actionability_label(row.error_category)}</div>
        </td>
        <td>{fmt_amount(row.amount, row.currency)}</td>
        <td>{row.country || '—'}</td>
        <td>{origins_label(row.origins)}</td>
        <td>
          <button
            type="button"
            title="Show every event behind this attempt"
            aria-label="Show events"
            onClick={() => this.setState({ open_group: row })}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 16,
              color: 'var(--text-muted)'
            }}
          >
            &#9432;
          </button>
        </td>
      </tr>
    ));

  render_body = () => {
    const { error, rows, total, page } = this.state;

    // An error must not look like a quiet day: this is the screen Care checks to find out whether
    // payments are breaking.
    if (error) {
      return (
        <div className="text-center" style={{ color: 'var(--state-error-text)' }}>
          Could not load payment failures. This is a load error, not a count of zero — retry, and
          raise it with the dev team if it persists.
        </div>
      );
    }

    if (rows.length === 0) {
      return (
        <>
          <div className="text-center mb-2">
            {total} payment attempt{total === 1 ? '' : 's'} failed
          </div>
          <div className="text-center">No failures in this range.</div>
        </>
      );
    }

    return (
      <>
        <div className="text-center mb-2">
          {total} payment attempt{total === 1 ? '' : 's'} failed
        </div>
        <table className="table-fixed">
          <thead>
            <tr>
              <th>Last attempt</th>
              <th>Student</th>
              <th>Attempts</th>
              <th>Step</th>
              <th>Reason</th>
              <th>Category</th>
              <th>Amount</th>
              <th>Country</th>
              <th>Seen by</th>
              <th aria-label="Events" />
            </tr>
          </thead>
          <tbody>{this.render_rows()}</tbody>
        </table>
        <Paginator
          curr_page={page}
          page_limit={Math.ceil(total / PAGE_SIZE)}
          pageChange={(p) => this.load_data(p)}
        />
      </>
    );
  };

  render() {
    const { loading, open_group } = this.state;
    return (
      <div className="common-page-container">
        <CommonHeader title="Payment Failures" />
        <div className="common-page-content">
          {this.render_filters()}
          {loading ? <div className="text-center">Loading…</div> : this.render_body()}
        </div>
        {open_group ? (
          <EventsModal
            group={open_group}
            iana_timezone={this.props.iana_timezone}
            start_date={this.state.start_date}
            end_date={this.state.end_date}
            date_filter_type={this.state.date_filter_type}
            set_notification_variable={this.props.set_notification_variable}
            close={() => this.setState({ open_group: null })}
          />
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ iana_timezone: state.home.iana_timezone });

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PaymentFailures));
